import React from 'react';
import { ChevronLeft, ChevronRight, Edit2, Trash2 } from 'lucide-react';

interface Column<T> {
    header: string;
    accessor: keyof T | ((item: T) => React.ReactNode);
    className?: string;
}

interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    total: number;
    page: number;
    limit: number;
    onPageChange: (page: number) => void;
    onEdit?: (item: T) => void;
    onDelete?: (item: T) => void;
    customActions?: (item: T) => React.ReactNode;
    loading?: boolean;
}

function DataTable<T extends { id: string }>({
    data,
    columns,
    total,
    page,
    limit,
    onPageChange,
    onEdit,
    onDelete,
    customActions,
    loading
}: DataTableProps<T>) {
    const totalPages = Math.ceil(total / limit);

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading...</div>;
    }

    const hasActions = !!(onEdit || onDelete || customActions);

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200">
                        <tr>
                            <th className="px-6 py-3 w-10">
                                <input type="checkbox" className="rounded border-gray-300" />
                            </th>
                            {columns.map((col, index) => (
                                <th key={index} className={`px-6 py-3 ${col.className || ''}`}>
                                    {col.header}
                                </th>
                            ))}
                            {hasActions && (
                                <th className="px-6 py-3 text-right">Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + (hasActions ? 2 : 1)} className="px-6 py-8 text-center text-gray-500">
                                    No records found
                                </td>
                            </tr>
                        ) : (
                            data.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <input type="checkbox" className="rounded border-gray-300" />
                                    </td>
                                    {columns.map((col, index) => (
                                        <td key={index} className="px-6 py-4">
                                            {typeof col.accessor === 'function'
                                                ? col.accessor(item)
                                                : (item[col.accessor] as React.ReactNode)}
                                        </td>
                                    ))}
                                    {hasActions && (
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {customActions && customActions(item)}
                                                {onEdit && (
                                                    <button onClick={() => onEdit(item)} className="text-blue-600 hover:text-blue-800 p-1">
                                                        <Edit2 size={16} />
                                                    </button>
                                                )}
                                                {onDelete && (
                                                    <button onClick={() => onDelete(item)} className="text-red-500 hover:text-red-700 p-1">
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => onPageChange(page - 1)}
                            disabled={page === 1}
                            className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={() => onPageChange(page + 1)}
                            disabled={page === totalPages}
                            className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
